import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Check, Loader2, PartyPopper } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { subscriptionAPI } from '../services/api';
import { toast } from 'sonner';

const SubscriptionSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking'); // checking, success, error
  const [message, setMessage] = useState('Verifying your payment...');
  const [attempts, setAttempts] = useState(0);
  const maxAttempts = 5;

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    } else {
      setStatus('error');
      setMessage('No session ID found. Please try again.');
    }
  }, [sessionId]);

  const pollPaymentStatus = async () => {
    if (attempts >= maxAttempts) {
      setStatus('error');
      setMessage('Payment verification timed out. Please check your email for confirmation or contact support.');
      return;
    }

    try {
      const result = await subscriptionAPI.checkPaymentStatus(sessionId);
      
      if (result.payment_status === 'paid' || result.status === 'paid') {
        setStatus('success');
        setMessage('Your subscription is now active!');
        toast.success('Welcome to Habit2Goals Premium!');
        return;
      } else if (result.status === 'expired') {
        setStatus('error');
        setMessage('Payment session expired. Please try again.');
        return;
      }

      // Continue polling
      setAttempts(prev => prev + 1);
      setTimeout(pollPaymentStatus, 2000);
      
    } catch (error) {
      console.error('Error checking payment:', error);
      setAttempts(prev => prev + 1);
      
      if (attempts + 1 >= maxAttempts) {
        setStatus('error');
        setMessage('Error verifying payment. Please check your email for confirmation.');
      } else {
        setTimeout(pollPaymentStatus, 2000);
      }
    }
  };

  return (
    <div className="max-w-lg mx-auto py-12" data-testid="subscription-success-page">
      <Card className="gradient-card">
        <CardHeader className="text-center">
          {status === 'checking' && (
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-blue-500/20 to-purple-600/20 flex items-center justify-center">
              <Loader2 className="w-10 h-10 text-blue-500 animate-spin" />
            </div>
          )}
          {status === 'success' && (
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center animate-bounce">
              <PartyPopper className="w-10 h-10 text-white" />
            </div>
          )}
          {status === 'error' && (
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-red-500/20 to-orange-600/20 flex items-center justify-center">
              <span className="text-4xl">!</span>
            </div>
          )}
          
          <CardTitle className="text-2xl">
            {status === 'checking' && 'Processing Payment...'}
            {status === 'success' && 'Welcome to Premium!'}
            {status === 'error' && 'Something went wrong'}
          </CardTitle>
        </CardHeader>
        
        <CardContent className="text-center space-y-6">
          <p className="text-slate-600 dark:text-slate-400">
            {message}
          </p>
          
          {status === 'success' && (
            <div className="space-y-4">
              <ul className="text-left space-y-2 bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
                {['Unlimited habits', 'Advanced analytics', 'CSV export', 'Social sharing'].map((feature, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm">
                    <Check className="w-4 h-4 text-green-500" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
              
              <Button
                onClick={() => navigate('/dashboard')}
                className="w-full bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white shadow-lg"
                data-testid="go-to-dashboard-button"
              >
                Start Building Habits
              </Button>
            </div>
          )}
          
          {status === 'error' && (
            <div className="space-y-4">
              <Button
                onClick={() => navigate('/subscription')}
                variant="outline"
                className="w-full"
                data-testid="try-again-button"
              >
                Try Again
              </Button>
              <Button
                onClick={() => navigate('/dashboard')}
                variant="ghost"
                className="w-full"
              >
                Go to Dashboard
              </Button>
            </div>
          )}
          
          {status === 'checking' && (
            <p className="text-sm text-slate-500">
              This may take a few seconds...
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SubscriptionSuccess;
